<script setup lang="ts">
import { getcurrentChosenElement, update_cms_values } from '~/layers/cms/app/plugins/cms-video-element'
import type { CmsData, CmsVideo } from '~/layers/cms/app/types/cmsTypes'
import { defaultVideoValues } from '~/layers/cms/app/types/cmsTypes'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import Select from 'primevue/select'
import InputNumber from 'primevue/inputnumber'
import { inject, onMounted, ref } from 'vue'
import { useChooseFile } from '~/layers/cms/app/composables/chooseFile'
import { getByPath, setByPath } from '~/layers/cms/app/utils/cmsDataHelper'
import { useState } from '#app'

const cmsData = useState<CmsData>('cmsData')
const cms_video = ref<CmsVideo>(defaultVideoValues)
const cms_data_path = ref<string[]>([])

interface DialogRef {
  value: {
    close(): void
  }
}
const dialogRef = inject<DialogRef>('dialogRef')
const closeDialog = () => {
  if (!dialogRef) return
  dialogRef.value.close()
}

const loadElementData = () => {
  const element = getcurrentChosenElement()
  if (!element) {
    closeDialog()
    return
  }
  cms_data_path.value = element
  cms_video.value = { ...defaultVideoValues, ...getByPath(cmsData.value, cms_data_path.value)?.component_data } as CmsVideo
}

onMounted(() => {
  loadElementData()
})

function updateVideo() {
  setByPath(cmsData.value, [...cms_data_path.value, 'component_data'], cms_video.value)
  update_cms_values(cms_video.value)
  closeDialog()
}

const { chooseFile } = useChooseFile()
const videoError = ref(false)
const selectFile = async (key: keyof CmsVideo) => {
  const newSrc = await chooseFile()
  if (newSrc) {
    (cms_video.value as Record<string, unknown>)[key] = newSrc
    if (key === 'video_path') videoError.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex cursor-pointer items-center gap-4"
      @click="selectFile('video_path')"
    >
      <label class="w-24 font-semibold">Video</label>
      <video
        v-if="cms_video.video_path && !videoError"
        :src="cms_video.video_path"
        :poster="cms_video.poster"
        class="max-w-100"
        @error="videoError = true"
      />
      <div v-else>
        Brak video
      </div>
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-autoplay"
        class="w-24 font-semibold"
      >Autoplay</label>
      <Checkbox
        id="input-autoplay"
        v-model="cms_video.autoplay"
        binary
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-muted"
        class="w-24 font-semibold"
      >Muted</label>
      <Checkbox
        id="input-muted"
        v-model="cms_video.muted"
        binary
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-loop"
        class="w-24 font-semibold"
      >Loop</label>
      <Checkbox
        id="input-loop"
        v-model="cms_video.loop"
        binary
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-controls"
        class="w-24 font-semibold"
      >Controls</label>
      <Checkbox
        id="input-controls"
        v-model="cms_video.controls"
        binary
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-playsinline"
        class="w-24 font-semibold"
      >Plays inline</label>
      <Checkbox
        id="input-playsinline"
        v-model="cms_video.playsinline"
        binary
      />
    </div>

    <div
      class="flex cursor-pointer items-center gap-4"
      @click="selectFile('poster')"
    >
      <label class="w-24 font-semibold">Poster</label>
      <NuxtImg
        v-if="cms_video.poster"
        :src="cms_video.poster"
        alt="poster"
        class="max-w-100 object-contain"
        width="400"
        height="400"
        format="webp"
        loading="lazy"
        quality="100"
      />
      <div v-else>
        Brak postera
      </div>
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-preload"
        class="w-24 font-semibold"
      >Preload</label>
      <Select
        id="input-preload"
        v-model="cms_video.preload"
        class="flex-auto"
        :options="['auto', 'metadata', 'none']"
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-width"
        class="w-24 font-semibold"
      >Szerokość</label>
      <InputNumber
        id="input-width"
        v-model="cms_video.width"
        class="flex-auto"
        placeholder="opcjonalne"
        :min="0"
      />
    </div>

    <div class="flex items-center gap-4">
      <label
        for="input-height"
        class="w-24 font-semibold"
      >Wysokość</label>
      <InputNumber
        id="input-height"
        v-model="cms_video.height"
        class="flex-auto"
        placeholder="opcjonalne"
        :min="0"
      />
    </div>

    <div class="flex justify-end gap-2">
      <Button
        type="button"
        label="Cancel"
        severity="secondary"
        @click="closeDialog"
      />
      <Button
        type="button"
        label="Save"
        @click="updateVideo"
      />
    </div>
  </div>
</template>
